/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import { TFunction } from 'i18next'
import { closeModal, openModal } from 'helpers/actions'
import { AppDispatch } from 'helpers/store'
import EditCompanyModal from 'components/Companies/EditCompanyModal'
import EditLeadModal from 'components/leads/EditLeadModal'
import { CompaniesAction } from 'store/companies/CompaniesAction'
import LeadsAction from 'store/leads/LeadsAction'
import OpportunitiesAction from 'store/opportunities/OpportunitiesAction'
import { Lead } from 'types/entities/Lead'
import { LeadCategory } from 'types/enums/LeadCategory'
import { ModalSize } from 'types/enums/ModalSize'
import { ReminderModelName } from 'types/enums/ReminderModelName'
import { toast } from 'utils/toastUtils'
import {
    getReminderModelRoute,
    normalizeReminderModelName,
} from 'utils/reminders/reminderUtils'

export type OpenEntityByModelParams = {
    dispatch: AppDispatch
    t: TFunction
    navigate: (path: string) => void
    modelName: string | null | undefined
    modelId: number | null | undefined
}

const openLeadOrOpportunityModal = async (
    dispatch: AppDispatch,
    t: TFunction,
    modelId: number,
    isOpportunity: boolean
) => {
    const action = isOpportunity ? OpportunitiesAction : LeadsAction
    const loadedLead = await dispatch(action.fetchLeadById({ id: modelId } as Lead))
    dispatch(
        openModal(
            <EditLeadModal lead={loadedLead} onSuccess={() => {}} />,
            {
                modalSize: ModalSize.RoundedFullscreen,
                closeModal: () => {
                    dispatch(action.resetSelectedLead())
                    dispatch(closeModal())
                },
            },
            loadedLead.category === LeadCategory.Opportunity || isOpportunity
                ? t('Edit Opportunity')
                : t('Edit Lead')
        )
    )
}

const openCompanyModal = async (dispatch: AppDispatch, t: TFunction, modelId: number) => {
    const company = await dispatch(CompaniesAction.fetchCompanyById(modelId))
    dispatch(
        openModal(
            <EditCompanyModal company={company} onSuccess={() => {}} />,
            {
                modalSize: ModalSize.RoundedFullscreen,
                closeModal: () => {
                    dispatch(CompaniesAction.resetSelectedCompany())
                    dispatch(closeModal())
                },
            },
            t('Edit Company')
        )
    )
}

/**
 * Opens the related CRM entity: modal for lead/opportunity/company, route for others.
 * Returns true when navigation/open was attempted.
 */
export const openEntityByModelName = async ({
    dispatch,
    t,
    navigate,
    modelName,
    modelId,
}: OpenEntityByModelParams): Promise<boolean> => {
    const normalized = normalizeReminderModelName(modelName)
    const id = modelId != null ? Number(modelId) : NaN
    if (!normalized || !Number.isFinite(id) || id <= 0) {
        return false
    }

    try {
        switch (normalized) {
            case ReminderModelName.Lead:
                await openLeadOrOpportunityModal(dispatch, t, id, false)
                return true
            case ReminderModelName.Opportunity:
                await openLeadOrOpportunityModal(dispatch, t, id, true)
                return true
            case ReminderModelName.Company:
                await openCompanyModal(dispatch, t, id)
                return true
            default: {
                const route = getReminderModelRoute(normalized, id)
                if (!route) {
                    return false
                }
                navigate(route)
                return true
            }
        }
    } catch {
        toast.error(t('An error occurred') as string)
        return false
    }
}
